import React, { useState } from 'react';
import Icon, { IconType } from '../Icon/Icon';

export interface ISigningElement {
  id: string;
  type: 'signature' | 'date' | 'checkmark' | 'x' | 'text';
  x: number;
  y: number;
  content?: string;
  pageNumber: number;
  textColor?: string;
}

export interface ISigningDefaults {
  defaultDate: string;
  defaultSignature: string;
  textColor: string;
}

interface IPdfSigningSidebarProps {
  selectedElement: ISigningElement | null;
  onAddElement: (type: ISigningElement['type']) => void;
  onDeleteElement: () => void;
  signingDefaults: ISigningDefaults;
  onDefaultsChange: (defaults: ISigningDefaults) => void;
  onApplySignatures?: () => void;
  hasSigningElements?: boolean;
}

const PdfSigningSidebar: React.FC<IPdfSigningSidebarProps> = ({
  selectedElement,
  onAddElement,
  onDeleteElement,
  signingDefaults,
  onDefaultsChange,
  onApplySignatures,
  hasSigningElements
}) => {
  const [showConfig, setShowConfig] = useState(false);
  
  const colorOptions = [
    { value: '#000000', label: 'Black' },
    { value: '#ff0000', label: 'Red' },
    { value: '#0066cc', label: 'Blue' },
    { value: '#000080', label: 'Navy' }
  ];
  const signingOptions = [
    { type: 'signature' as const, icon: IconType.sign, label: 'Signature' },
    { type: 'date' as const, icon: IconType.calendar, label: 'Date' },
    { type: 'x' as const, icon: IconType.close, label: 'X Mark' },
    { type: 'text' as const, icon: IconType.textMsg, label: 'Free Text' }
  ];

  const handleDefaultChange = (field: keyof ISigningDefaults, value: string) => {
    onDefaultsChange({
      ...signingDefaults,
      [field]: value
    });
  };

  return (
    <div className="pdf-signing-sidebar">
      <div className="sidebar-header">
        <h3>Signing Tools</h3>
        <button 
          className="config-toggle-btn"
          onClick={() => setShowConfig(!showConfig)}
          title="Default Settings"
        >
          <Icon type={IconType.configure} />
        </button>
      </div>
      
      <div className="signing-options">
        {signingOptions.map((option) => (
          <button
            key={option.type}
            className="signing-option-btn"
            onClick={() => onAddElement(option.type)}
            title={`Add ${option.label}`}
          >
            <Icon type={option.icon} />
            <span>{option.label}</span>
          </button>
        ))}
      </div>

      {showConfig && (
        <div className="config-section">
          <div className="config-header">
            <h4>Default Settings</h4>
          </div>
          
          <div className="config-field">
            <label htmlFor="default-date">Default Date:</label>
            <input
              id="default-date"
              type="date"
              value={signingDefaults.defaultDate}
              onChange={(e) => handleDefaultChange('defaultDate', e.target.value)}
              className="config-input"
            />
          </div>

          <div className="config-field">
            <label htmlFor="default-signature">Default Signature:</label>
            <input
              id="default-signature"
              type="text"
              value={signingDefaults.defaultSignature}
              onChange={(e) => handleDefaultChange('defaultSignature', e.target.value)}
              placeholder="Enter signature text"
              className="config-input"
            />
          </div>

          <div className="config-field">
            <label>Text Color:</label>
            <div className="color-options">
              {colorOptions.map((color) => (
                <button
                  key={color.value}
                  className={`color-option ${signingDefaults.textColor === color.value ? 'selected' : ''}`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => handleDefaultChange('textColor', color.value)}
                  title={color.label}
                >
                  {signingDefaults.textColor === color.value && (
                    <Icon type={IconType.check} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {selectedElement && (
        <div className="selected-element-actions">
          <div className="selected-element-info">
            <h4>Selected Element</h4>
            <p>{selectedElement.type.charAt(0).toUpperCase() + selectedElement.type.slice(1)}</p>
          </div>
          <button
            className="delete-element-btn"
            onClick={onDeleteElement}
            title="Delete selected element"
          >
            <Icon type={IconType.trashO} />
            <span>Delete</span>
          </button>
        </div>
      )}

      {onApplySignatures && hasSigningElements && (
        <div className="apply-signatures-section">
          <button
            className="apply-signatures-btn"
            onClick={onApplySignatures}
            title="Apply all signatures to PDF"
          >
            <Icon type={IconType.check} />
            <span>Apply Signatures</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PdfSigningSidebar;